// #region Imports
import Divider from "@mui/material/Divider";
import FormControlLabel from "@mui/material/FormControlLabel";
import Grid from "@mui/material/Grid";
import { styled } from "@mui/material/styles";
import Switch from "@mui/material/Switch";
import Typography from "@mui/material/Typography";
import { ConfirmResponse, useConfirmDialog } from "_shared/_providers/dialogs/confirm/confirm-dialog";
import downloadFile from "_shared/_utilities/download-file";
import AHBoolean from "_shared/booleans/boolean";
import Guid from "_shared/guids/guid";
import { useLazyDownloadDocumentQuery } from "documents/_api/document-api";
import DocumentSelector from "documents/_views/selectors/document-selector";
import DocumentUploadOverlay from "documents/_views/uploader/document-upload-overlay";
import Document from "documents/document";
import { enqueueSnackbar } from "notistack";
import React, { useCallback, useMemo, useState } from "react";
import { Masked } from "work/masked-fields";
import ProposalBuilder from "work/proposals/__builder/proposal-builder";
import {
  fieldHasPendingChange,
  pendingChangeContainerStyles,
} from "work/proposals/comments/_view/pending-change-field-highlight";
import { IssueContainer } from "work/proposals/issues/_views/proposal-issue-container";
import ProposalIssues, { ProposalIssueLevel } from "work/proposals/issues/proposal-issues";
import ProposalFieldHistoryButton from "work/proposals/negotiation/proposal-field-history-button";
import Proposal from "work/proposals/proposal";
import ProposalField from "work/proposals/proposal-fields/proposal-field";
import { ReviewerEditLockIcon } from "work/proposals/reviewers/_views/reviewer-edit-lock-feedback";
import SelectedDocumentList from "work/work-agreements/contracts/_views/selected-document-list";
import { WorkDocumentType } from "work/work-agreements/contracts/work-document-type";
import WorkDocument from "work/work-agreements/contracts/work-document";
import { xor } from "lodash";
import ResizableTemplatePanel from "work/proposals/_views/_shared/resizable-template-panel";
import { MaskedProposalField, maskedProposalFieldNotice } from "work/proposals/_views/_shared/masked-field-notice";
import {
  paneIsCompact,
  workPaneNarrowerThan,
  workPaneProseInset,
} from "work/_views/work-pane-layout";
import { useWorkPaneIsCompact } from "work/_views/use-work-pane-size";
// #endregion

// #region Styled Components
const DOCUMENT_ROW_MAX_WIDTH = "550px";

const ListContainer = styled(Grid, {
  shouldForwardProp: (prop) => prop !== "waived",
})<{ waived?: boolean }>(({ theme, waived }) => ({
  flex: 1,
  flexWrap: "nowrap",
  height: "100%",
  minHeight: "20rem",
  opacity: waived ? 0.25 : 1,
  pointerEvents: waived ? "none" : "auto",
  width: "100%",
  [paneIsCompact(theme)]: {
    flexDirection: "column",
    flexWrap: "nowrap",
    height: "auto",
    minHeight: 0,
  },
}));
/**
 * The templates column, dimmed while the collection it feeds is waived.
 *
 * Only the dimming lives here. `ResizableTemplatePanel` owns the column's width, its collapse to a
 * rail when a side pane opens, and what it becomes on a compact pane — the same arrangement the fee
 * schedule and team tabs get, which this tab used to half-copy from its own styles.
 */
const DocumentSelectorContainer = styled(ResizableTemplatePanel, {
  shouldForwardProp: (prop) => prop !== "waived",
})<{ waived?: boolean }>(({ waived }) => ({
  opacity: waived ? 0.25 : 1,
  pointerEvents: waived ? "none" : "auto",
}));
const SelectorDivider = styled(Grid, {
  shouldForwardProp: (prop) => prop !== "waived",
})<{ waived?: boolean }>(({ theme, waived }) => ({
  opacity: waived ? 0.25 : 1,
  pointerEvents: waived ? "none" : "auto",
  [paneIsCompact(theme)]: {
    display: "none",
  },
}));
const SelectedDocsList = styled(SelectedDocumentList)(({ theme }) => ({
  [workPaneNarrowerThan(theme.breakpoints.values.lg)]: {
    marginTop: theme.spacing(2),
  },
  maxHeight: "60vh",
  // No left margin: the table meets the resize divider, the way the fee schedule's does. The prose
  // above it carries its own inset instead — see `workPaneProseInset`.
  minWidth: 0,
  overflowY: "auto",
  position: "relative",
  flex: 1,
  [paneIsCompact(theme)]: {
    boxSizing: "border-box",
    marginLeft: 0,
    maxHeight: "none",
    maxWidth: "none",
    minWidth: 0,
    width: "100%",
    "&&": {
      marginLeft: 0,
      maxWidth: "none",
      minWidth: 0,
      width: "100%",
    },
  },
}));
const HeadControls = styled("div")(({ theme }) => ({
  ...workPaneProseInset(theme),
  alignItems: "flex-start",
  boxSizing: "border-box",
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(1),
  maxWidth: DOCUMENT_ROW_MAX_WIDTH,
  paddingTop: theme.spacing(1),
  width: "100%",
  [paneIsCompact(theme)]: {
    maxWidth: "none",
  },
}));
const SwitchControls = styled("div")(({ theme }) => ({
  [paneIsCompact(theme)]: {
    paddingRight: 0,
  },
  alignItems: "center",
  display: "flex",
  justifyContent: "space-between",
  paddingRight: theme.spacing(1),
  width: "100%",
}));
const FieldActions = styled("div")(() => ({
  alignItems: "center",
  display: "flex",
  flexShrink: 0,
}));
const PendingChangeControlFrame = styled("span", {
  shouldForwardProp: (prop) => prop !== "pendingChange",
})<{ pendingChange?: boolean }>(({ theme, pendingChange }) => ({
  alignItems: "center",
  display: "inline-flex",
  gap: theme.spacing(1),
  ...(pendingChange && pendingChangeContainerStyles(theme)),
}));
const WaivedContent = styled("div", {
  shouldForwardProp: (prop) => prop !== "waived",
})<{ waived: boolean }>(({ waived }) => ({
  display: "flex",
  flex: 1,
  minWidth: 0,
  opacity: waived ? 0.25 : 1,
  pointerEvents: waived ? "none" : "auto",
  width: "100%",
}));
const SelectedDocsPanel = styled("div")(() => ({
  display: "flex",
  flex: 1,
  flexDirection: "column",
  minWidth: 0,
  width: "100%",
}));
const Uploader = styled(DocumentUploadOverlay)(() => ({
  backgroundColor: "rgba(250, 250, 250, 0.5)",
  backdropFilter: "blur(5px) saturate(200%)",
  position: "absolute",
  top: 0,
  left: 0,
  margin: "1rem",
  width: "calc(100% - 2rem)",
  height: "calc(100% - 2rem)",
  zIndex: 9999,
}));
// #endregion

type ConflictsUploadProps = {
  activeReviewField?: ProposalField;
  proposalBuilder: ProposalBuilder;
  proposalId?: Guid;
  issues?: ProposalIssues;
  proposal?: Proposal;
  negotiationId?: string;
  viewerScope?: string;
  disableEditing?: boolean;
  fieldLockTooltip?: string;
  /** A side pane is taking the row's width, so the templates column gives up its own — see `ResizableTemplatePanel`. */
  isDrawerOpen: boolean;
  showPendingChangeHighlights?: boolean;
  onProposalBuilderUpdated: (updatedProposalBuilder: ProposalBuilder) => void;
};

export default function ConflictsTab(props: Readonly<ConflictsUploadProps>) {
  const {
    activeReviewField,
    proposalBuilder,
    proposalId,
    negotiationId,
    viewerScope,
    disableEditing,
    isDrawerOpen,
    fieldLockTooltip,
    onProposalBuilderUpdated,
    showPendingChangeHighlights,
  } = props;

  // #region State
  // The template selector collapses to its summary when there is no room to hold it open. The pane
  // is what decides that here, not the window — an open comments panel takes a column out of it.
  const selectorIsCompact = useWorkPaneIsCompact();
  const [creatingDocumentFromTemplate, setCreatingDocumentFromTemplate] = useState<Guid | null>(null);
  const [isDownloadingFile, setIsDownloadingFile] = useState<Guid | null>(null);
  
  const pendingChangeFields = showPendingChangeHighlights
    ? proposalBuilder.sessionHistory?.flatMap((change) => change.field ? [change.field] : []) ?? []
    : [];
  const showLockIcon = Boolean(disableEditing && fieldLockTooltip);

  // #region Hooks
  const confirm = useConfirmDialog();
  const [downloadDocument] = useLazyDownloadDocumentQuery();

  // #region Memoized values
  const issues = useMemo(
    () =>
      props.issues?.entries.filter((issue) => {
        const candidates = [
          ProposalField.Conflicts,
          ProposalField.ConflictsDocument(issue.field.id ?? Guid.EMPTY),
          ProposalField.WaiveConflictsCheck,
        ];

        return candidates.some((candidate) => candidate.isEqualTo(issue.field));
      }),
    [props.issues?.entries]
  );

  const hasActiveReviewFieldIssue = useMemo(() => {
    return issues?.some((issue) => issue.field.isEqualTo(activeReviewField)) ?? false;
  }, [issues, activeReviewField]);

  const conflictsCheckWaived = useMemo(() => {
    if (Masked.isMasked(proposalBuilder.currentSpec.workAgreement.conflictsCheckWaived)) return false;
    return proposalBuilder.currentSpec.workAgreement.conflictsCheckWaived?.value ?? false;
  }, [proposalBuilder.currentSpec.workAgreement.conflictsCheckWaived]);

  const selectedDocuments = useMemo(() => {
    if (Masked.isMasked(proposalBuilder.currentSpec.workAgreement.conflicts)) return [];
    return proposalBuilder.currentSpec.workAgreement.conflicts ?? [];
  }, [proposalBuilder.currentSpec.workAgreement.conflicts]);

  const selectedDocumentIds = useMemo(
    () => selectedDocuments.map((doc) => doc.id),
    [selectedDocuments]
  );

  const selectedTemplateIds = useMemo(
    () => selectedDocuments.flatMap((doc) => doc.templateIds),
    [selectedDocuments]
  );
  // #endregion

  // #region Functions
  const handleConflictsDocumentRemoved = useCallback(async (documentId: Guid): Promise<void> => {
    const response = await confirm({
      title: "Remove Conflicts Document",
      message: "Are you sure you want to remove this conflicts document?",
    });

    if (
      response === ConfirmResponse.Cancel ||
      Masked.isMasked(proposalBuilder.currentSpec.workAgreement.conflicts)
    )
      return;

    onProposalBuilderUpdated(
      proposalBuilder.setConflicts(
        (proposalBuilder.currentSpec.workAgreement.conflicts ?? []).filter(
          (doc) => !doc.id.isEqualTo(documentId)
        )
      )
    );
  }, [confirm, onProposalBuilderUpdated, proposalBuilder]);

  const handleDownloadDocumentById = useCallback(
    async (documentId: Guid, event?: React.MouseEvent<HTMLButtonElement, MouseEvent>): Promise<void> => {
      event?.stopPropagation();
      try {
        setIsDownloadingFile(documentId);
        const conflictsDocument = await downloadDocument({ documentId: documentId.value }).unwrap();
        await downloadFile(conflictsDocument);
      } catch (err) {
        console.error(err);
        enqueueSnackbar("Failed to download conflicts document. Please try again", { variant: "error" });
      } finally {
        setIsDownloadingFile(null);
      }
    }, [downloadDocument]);

  const handleDocumentSelectionChanged = useCallback((selectedDocs: Document[], deselectedDocs: Document[]) => {
    if (Masked.isMasked(proposalBuilder.currentSpec?.workAgreement.conflicts)) {
      throw new Error("You do not have permission to view contracts.");
    }

    let existingDocuments = [...(proposalBuilder.currentSpec?.workAgreement.conflicts ?? [])];
    let currentDocuments = [...existingDocuments];

    for (const selectedDoc of selectedDocs) {
      if (!currentDocuments.some((doc) => doc.id.isEqualTo(selectedDoc.id))) {
        currentDocuments.push(
          WorkDocument.fromDocument(
            selectedDoc, 
            WorkDocumentType.Conflicts
          )
        );
      }
    }

    for (const deselectedDoc of deselectedDocs) {
      currentDocuments = currentDocuments.filter((doc) => !doc.id.isEqualTo(deselectedDoc.id));
    }

    // If the document lists are the same, do not trigger an update
    const existingDocIds = existingDocuments.map(d => d.id.value);
    const currentDocIds = currentDocuments.map(d => d.id.value);

    if (xor(existingDocIds, currentDocIds).length === 0) return;

    onProposalBuilderUpdated(proposalBuilder.setConflicts(currentDocuments));
  }, [onProposalBuilderUpdated, proposalBuilder]);

  const handleConflictsDocumentsAdded = useCallback((documents: Document[]) => {
    if (Masked.isMasked(proposalBuilder.currentSpec?.workAgreement.conflicts)) {
      throw new Error("You do not have permission to view contracts.");
    }

    if (documents.length === 0) return;

    const updatedDocuments = [...(proposalBuilder.currentSpec.workAgreement.conflicts ?? [])];
    for (const document of documents) {
      if (updatedDocuments.some((existingDocument) => existingDocument.id.isEqualTo(document.id))) continue;

      updatedDocuments.push(WorkDocument.fromDocument(document, WorkDocumentType.Conflicts));
    }

    setCreatingDocumentFromTemplate(null);
    onProposalBuilderUpdated(proposalBuilder.setConflicts(updatedDocuments));
  }, [onProposalBuilderUpdated, proposalBuilder]);

  const handleBeginCreateDocumentFromTemplate = useCallback(async (templateId: Guid | undefined | null) => {
    if (!templateId) return;
    setCreatingDocumentFromTemplate(templateId);
    await handleDownloadDocumentById(templateId);
  }, [handleDownloadDocumentById]);

  // A revert restores a document's prior value through the SAME `setConflicts` builder path any other
  // document edit uses, so it flows to `queueProposalUpdate` as a `CollectionItemUpdated`/
  // `CollectionItemAdded` op — never a raw `FieldSet`.
  const handleConflictsDocumentReverted = useCallback((document: WorkDocument) => {
    if (Masked.isMasked(proposalBuilder.currentSpec?.workAgreement.conflicts)) return;

    const existingDocuments = proposalBuilder.currentSpec.workAgreement.conflicts ?? [];
    const updatedDocuments = existingDocuments.some((existing) => existing.id.isEqualTo(document.id))
      ? existingDocuments.map((existing) => (existing.id.isEqualTo(document.id) ? document : existing))
      : [...existingDocuments, document];

    onProposalBuilderUpdated(proposalBuilder.setConflicts(updatedDocuments));
  }, [onProposalBuilderUpdated, proposalBuilder]);

  const handleConflictsCheckWaiverChanged = useCallback(async (checked: AHBoolean) => {
    if (Masked.isMasked(proposalBuilder.currentSpec.workAgreement?.conflicts)) {
      throw new Error("You do not have permission to view contracts.");
    }
    if (proposalBuilder.currentSpec.workAgreement?.conflicts?.length) {
      const response = await confirm({
        title: "Remove Conflicts Documents?",
        message: "Waiving the conflicts check will remove the documents that are already on the proposal",
      });

      if (response === ConfirmResponse.Cancel) return;
    }

    onProposalBuilderUpdated(proposalBuilder.setConflictsCheckWaived(checked).setConflicts([]));
  }, [confirm, onProposalBuilderUpdated, proposalBuilder]);

  // #region Render
  if ((Masked.isMasked(proposalBuilder.currentSpec.workAgreement.conflicts)) ||
    (Masked.isMasked(proposalBuilder.currentSpec.workAgreement.conflictsCheckWaived))) {
    return (
      <Typography variant="body1">{maskedProposalFieldNotice(MaskedProposalField.Conflicts)}</Typography>
    );
  }
  return (
    <>
      <ListContainer container direction="row">
        {!disableEditing && (
          <>
            <DocumentSelectorContainer minimized={isDrawerOpen} waived={conflictsCheckWaived}>
              <DocumentSelector
                compact={selectorIsCompact}
                documentType="conflicts"
                proposalId={proposalId}
                selectedDocumentIds={selectedDocumentIds}
                selectedTemplateIds={selectedTemplateIds}
                onDocumentSelectionChanged={handleDocumentSelectionChanged}
                onCreateDocumentFromTemplate={handleBeginCreateDocumentFromTemplate}
              />
            </DocumentSelectorContainer>
            <SelectorDivider waived={conflictsCheckWaived}>
              <Divider orientation="vertical" />
            </SelectorDivider>
          </>
        )}
        <SelectedDocsPanel>
          <HeadControls>
            {(issues?.length ?? 0) > 0 && (
              <IssueContainer active={hasActiveReviewFieldIssue}>
                {issues?.map((issue) => (
                  <Typography
                    key={issue.field?.cacheKey}
                    color={issue.level === ProposalIssueLevel.Critical ? "error" : "warning"}
                    variant="body1"
                  >
                    {issue.description}
                  </Typography>
                ))}
              </IssueContainer>
            )}
            <SwitchControls>
              <PendingChangeControlFrame
                pendingChange={fieldHasPendingChange(pendingChangeFields, ProposalField.WaiveConflictsCheck)}
              >
                {showLockIcon && <ReviewerEditLockIcon title={fieldLockTooltip} />}
                <FormControlLabel
                  control={
                    <Switch
                      disabled={disableEditing}
                      checked={conflictsCheckWaived}
                      onChange={(_event, checked) => handleConflictsCheckWaiverChanged(new AHBoolean(checked))}
                      color="primary"
                    />
                  }
                  label={
                    conflictsCheckWaived ? (
                      <strong>Conflicts Check Waived</strong>
                    ) : (
                      "Conflicts Check Not Waived"
                    )
                  }
                />
              </PendingChangeControlFrame>
              <FieldActions>
                <ProposalFieldHistoryButton
                  field={ProposalField.Conflicts}
                  negotiationId={negotiationId}
                  viewerScope={viewerScope}
                />
              </FieldActions>
            </SwitchControls>
          </HeadControls>
          <WaivedContent waived={conflictsCheckWaived}>
            <SelectedDocsList
              pendingChangeFields={pendingChangeFields}
              documents={selectedDocuments}
              documentType={WorkDocumentType.Conflicts}
              proposalId={proposalId}
              downloadingFileId={isDownloadingFile}
              disableEditing={disableEditing}
              fieldLockTooltip={fieldLockTooltip}
              conflictsCheckWaived={conflictsCheckWaived}
              negotiationId={negotiationId}
              viewerScope={viewerScope}
              onDownload={handleDownloadDocumentById}
              onDocumentRemoved={handleConflictsDocumentRemoved}
              onDocumentAdded={(document) => handleConflictsDocumentsAdded([document])}
              onDocumentsAdded={handleConflictsDocumentsAdded}
              onDocumentReverted={handleConflictsDocumentReverted}
            />
          </WaivedContent>
        </SelectedDocsPanel>
      </ListContainer>
      {!disableEditing && creatingDocumentFromTemplate && (
        <Uploader
          documentType={WorkDocumentType.Conflicts}
          proposalId={proposalId}
          templateId={creatingDocumentFromTemplate}
          onDocumentUploaded={(document) => handleConflictsDocumentsAdded([document])}
          onClose={() => setCreatingDocumentFromTemplate(null)}
        />
      )}
    </>
  );
}
